// Compass heading of the BACK CAMERA's pointing direction, degrees clockwise
// from north. The fiddly part of M1 (plan §4.3) — instrumented for field tuning.
//
// iOS Safari: `webkitCompassHeading` (already the device heading; valid when its
// companion `webkitCompassAccuracy` is 0..50). Requires the DeviceOrientation
// permission we request in ensureMotionPermission().
// Android Chrome: `deviceorientationabsolute` alpha/beta/gamma → W3C-spec formula
// for the heading of the device's back vector.
// Test override: `?fakeheading=deg`.

export interface HeadingSample {
  headingDeg: number
  source: 'webkit' | 'absolute' | 'fake'
  timestamp: number
}

export interface HeadingTracker {
  readonly current: HeadingSample | null
  /** Circular mean over the stability window, or null if too few samples. */
  readonly meanDeg: number | null
  /** Max angular deviation from the mean over the window (deg). */
  readonly spreadDeg: number | null
  readonly sampleCount: number
  stop: () => void
}

const WINDOW_MS = 1500

type IOSOrientationEvent = DeviceOrientationEvent & {
  webkitCompassHeading?: number
  webkitCompassAccuracy?: number
}

const DEG = Math.PI / 180

// W3C DeviceOrientation spec example: compass heading of the vector pointing out
// of the BACK of the device, from intrinsic ZXY angles.
export function headingFromEuler(alpha: number, beta: number, gamma: number): number {
  const _x = beta * DEG
  const _y = gamma * DEG
  const _z = alpha * DEG
  const cY = Math.cos(_y)
  const cZ = Math.cos(_z)
  const sX = Math.sin(_x)
  const sY = Math.sin(_y)
  const sZ = Math.sin(_z)
  const Vx = -cZ * sY - sZ * sX * cY
  const Vy = -sZ * sY + cZ * sX * cY
  let heading = Math.atan(Vx / Vy)
  if (Vy < 0) heading += Math.PI
  else if (Vx < 0) heading += 2 * Math.PI
  return heading / DEG
}

function circularMeanDeg(values: number[]): number {
  let sx = 0
  let sy = 0
  for (const v of values) {
    sx += Math.cos(v * DEG)
    sy += Math.sin(v * DEG)
  }
  return ((Math.atan2(sy, sx) / DEG) + 360) % 360
}

export function angularDiffDeg(a: number, b: number): number {
  const d = ((a - b + 540) % 360) - 180
  return Math.abs(d)
}

export function startHeading(): HeadingTracker {
  const samples: HeadingSample[] = []
  let listeners: Array<[string, EventListener]> = []
  let fakeTimer: ReturnType<typeof setInterval> | null = null

  const push = (headingDeg: number, source: HeadingSample['source']) => {
    const now = Date.now()
    samples.push({headingDeg: ((headingDeg % 360) + 360) % 360, source, timestamp: now})
    while (samples.length && samples[0].timestamp < now - WINDOW_MS * 2) samples.shift()
  }

  const fake = new URLSearchParams(window.location.search).get('fakeheading')
  if (fake !== null && Number.isFinite(Number(fake))) {
    const deg = Number(fake)
    push(deg, 'fake')
    fakeTimer = setInterval(() => push(deg, 'fake'), 250)
  } else {
    const onIOS = (event: Event) => {
      const e = event as IOSOrientationEvent
      if (typeof e.webkitCompassHeading !== 'number') return
      const acc = e.webkitCompassAccuracy
      if (typeof acc === 'number' && (acc < 0 || acc > 50)) return // invalid/uncalibrated
      push(e.webkitCompassHeading, 'webkit')
    }
    const onAbsolute = (event: Event) => {
      const e = event as DeviceOrientationEvent
      // Skip if iOS already feeds us (webkit events also fire 'deviceorientation').
      if ((e as IOSOrientationEvent).webkitCompassHeading !== undefined) return
      if (!e.absolute && event.type !== 'deviceorientationabsolute') return
      if (e.alpha === null || e.beta === null || e.gamma === null) return
      push(headingFromEuler(e.alpha, e.beta, e.gamma), 'absolute')
    }
    listeners = [
      ['deviceorientation', onIOS as EventListener],
      ['deviceorientationabsolute', onAbsolute as EventListener],
      ['deviceorientation', onAbsolute as EventListener],
    ]
    for (const [type, fn] of listeners) window.addEventListener(type, fn, true)
  }

  const windowSamples = () => {
    const cutoff = Date.now() - WINDOW_MS
    return samples.filter((s) => s.timestamp >= cutoff)
  }

  return {
    get current() {
      return samples.length ? samples[samples.length - 1] : null
    },
    get meanDeg() {
      const w = windowSamples()
      return w.length >= 4 ? circularMeanDeg(w.map((s) => s.headingDeg)) : null
    },
    get spreadDeg() {
      const w = windowSamples()
      if (w.length < 4) return null
      const mean = circularMeanDeg(w.map((s) => s.headingDeg))
      return Math.max(...w.map((s) => angularDiffDeg(s.headingDeg, mean)))
    },
    get sampleCount() {
      return samples.length
    },
    stop: () => {
      for (const [type, fn] of listeners) window.removeEventListener(type, fn, true)
      if (fakeTimer !== null) clearInterval(fakeTimer)
    },
  }
}
